import { IReckonInRoomCredentials } from '@era/locations-sdk';
import { createFeatureSelector, createSelector, MemoizedSelector, Selector } from '@ngrx/store';
import { Dictionary } from 'lodash';
import { InRoomCredentialsStoreSelectors } from '../in-room-credentials';
import { Category } from '../router/category';
import * as fromCategoryInRoomCredentials from './category-in-room-credentials.reducer';
import { CategoryInRoomCredentialsState, InRoomCredentialsConfiguration } from './category-in-room-credentials.state';

// ============================================================================
// STATE SELECTORS
// ============================================================================

export const selectCategoryInRoomCredentialsState =
  createFeatureSelector<CategoryInRoomCredentialsState>('categoryInRoomCredentials');

export const selectAllCategoryInRoomCredentials = createSelector(
  selectCategoryInRoomCredentialsState,
  fromCategoryInRoomCredentials.selectAllCategoryInRoomCredentials
);

export const selectCategoryInRoomCredentialsEntities: Selector<
  object,
  Dictionary<InRoomCredentialsConfiguration>
> = createSelector(
  selectCategoryInRoomCredentialsState,
  fromCategoryInRoomCredentials.selectCategoryInRoomCredentialsEntities
);

// ============================================================================
// FACTORY FUNCTIONS
// ============================================================================

export const createSelectInRoomCredentialsLinkedToCategory = (category: Category) => {
  return createSelector(
    selectCategoryInRoomCredentialsEntities,
    (configurations: Dictionary<InRoomCredentialsConfiguration>) => {
      if (!category || !configurations) {
        return undefined;
      }
      const key = `${category.categoryType}-${category.categoryId}`;
      return configurations[key];
    }
  );
};

// ============================================================================
// GENERIC SELECTORS
// ============================================================================

/**
 * Generic selector for checking if in-room credentials are loaded for any category type.
 *
 * Usage in containers:
 * this.credentialsLoaded$ = this.store.select(selectInRoomCredentialsLinkedToCategoryLoaded(category));
 */
export const selectInRoomCredentialsLinkedToCategoryLoaded = (
  category: Category
): MemoizedSelector<object, boolean> => {
  return createSelector(createSelectInRoomCredentialsLinkedToCategory(category), (configuration) => !!configuration);
};

/**
 * Generic selector for in-room credentials linked to any category type.
 *
 * Usage in containers:
 * this.credentials$ = this.store.select(selectInRoomCredentialsLinkedToCategory(category));
 */
export const selectInRoomCredentialsLinkedToCategory = (
  category: Category
): MemoizedSelector<object, IReckonInRoomCredentials | undefined> => {
  return createSelector(
    createSelectInRoomCredentialsLinkedToCategory(category),
    InRoomCredentialsStoreSelectors.selectInRoomCredentialsEntities,
    (configuration: InRoomCredentialsConfiguration, credentials) =>
      credentials && configuration ? credentials[configuration.iReckonInRoomCredentialsId] : undefined
  );
};

/**
 * Generic selector for checking if in-room credentials are linked to any category type.
 *
 * Usage in containers:
 * this.hasCredentials$ = this.store.select(selectHasInRoomCredentialsLinkedToCategory(category));
 */
export const selectHasInRoomCredentialsLinkedToCategory = (category: Category): MemoizedSelector<object, boolean> => {
  return createSelector(selectInRoomCredentialsLinkedToCategory(category), (credentials) => !!credentials);
};

/**
 * Generic selector for in-room credentials applied to any category type (with inheritance).
 * Automatically handles inheritance logic based on category.parent.
 *
 * Usage in containers:
 * this.appliedCredentials$ = this.store.select(selectInRoomCredentialsAppliedToCategory(category));
 */
export const selectInRoomCredentialsAppliedToCategory = (
  category: Category
): MemoizedSelector<object, IReckonInRoomCredentials | undefined> => {
  if (category.parent) {
    return createSelector(
      selectInRoomCredentialsAppliedToCategory(category.parent),
      selectInRoomCredentialsLinkedToCategory(category),
      (parentCredentials, currentCredentials) => currentCredentials ?? parentCredentials
    );
  } else {
    return selectInRoomCredentialsLinkedToCategory(category);
  }
};
